/*
 * Small, cheap branching drawings: loader colonies and the transition strip.
 *
 * Pure: no DOM, no React.
 *
 * miniColony(seed, { cx, cy, n, radius, depth = 2, yMin, yMax })
 *   -> { paths: [{ d, w, len, from }], tips: [{ x, y }] }
 *   n leading hyphae radiate from (cx, cy), six meandering segments each; with
 *   depth >= 2, laterals branch at the 2nd and 4th segments on alternating
 *   sides. y is clamped to [yMin, yMax] (a soil surface, say). Memoised.
 *   `len` is a path's length (for a dash reveal) and `from` how far along its
 *   leader it starts: 0 for a leader, 2/6 or 4/6 for a lateral -- so a drawing
 *   can grow each lateral only once the leader's tip has passed it.
 *
 * sweepPattern(pattern, W, seed)
 *   -> { main: { d, len }, branches: [{ d, len, at }], ticks: [{ d, at }],
 *        spore: null | { cx, cy }, pin: null | { x } }
 *   One hypha across a 12-px strip at y = 6. `at` is a fraction of W: where
 *   along the strip a feature sits, so it can appear just after the tip passes.
 *   pattern: 'branches' | 'roots' | 'septate' | 'spore'.
 */
import { makeRng } from './prng.js';
import { clamp } from './easing.js';

const DEG = Math.PI / 180;
const cache = new Map();

const f1 = (n) => {
  const v = Math.round(n * 10) / 10;
  return String(Object.is(v, -0) ? 0 : v);
};

function polyline(pts) {
  let d = '';
  for (let i = 0; i < pts.length; i++) d += `${i ? 'L' : 'M'}${f1(pts[i][0])} ${f1(pts[i][1])}`;
  return d;
}

function polyLength(pts) {
  let len = 0;
  for (let i = 1; i < pts.length; i++) len += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
  return Math.ceil(len) + 1;
}

export function miniColony(seed, { cx, cy, n, radius, depth = 2, yMin = -Infinity, yMax = Infinity }) {
  const key = [seed, cx, cy, n, radius, depth, yMin, yMax].join('|');
  const hit = cache.get(key);
  if (hit) return hit;

  const rng = makeRng(seed);
  const cy0 = clamp(cy, yMin, yMax);
  const paths = [];
  const tips = [];
  let side = 1;
  for (let i = 0; i < n; i++) {
    let a = (i * 2 * Math.PI) / n + rng.range(-0.2, 0.2);
    let x = cx;
    let y = cy0;
    const pts = [[x, y]];
    const seg = radius / 6;
    for (let k = 1; k <= 6; k++) {
      a += rng.range(-0.15, 0.15);
      x += Math.cos(a) * seg;
      y = clamp(y + Math.sin(a) * seg, yMin, yMax);
      pts.push([x, y]);
      if (depth >= 2 && (k === 2 || k === 4)) {
        let b = a + side * rng.range(45, 65) * DEG;
        side = -side;
        let lx = x;
        let ly = y;
        const lat = [[lx, ly]];
        for (let j = 0; j < 3; j++) {
          b += rng.range(-0.12, 0.12);
          lx += Math.cos(b) * radius * 0.13;
          ly = clamp(ly + Math.sin(b) * radius * 0.13, yMin, yMax);
          lat.push([lx, ly]);
        }
        paths.push({ d: polyline(lat), w: 0.8, len: polyLength(lat), from: k / 6 });
      }
    }
    paths.push({ d: polyline(pts), w: 1.1, len: polyLength(pts), from: 0 });
    tips.push({ x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 });
  }
  const out = { paths, tips };
  if (cache.size > 64) cache.clear();
  cache.set(key, out);
  return out;
}

export function sweepPattern(pattern, W, seed) {
  const rng = makeRng(seed);
  const x0 = pattern === 'spore' ? 6 : 0;
  const pts = [];
  for (let x = x0; x < W; x += 24) pts.push([x, 6 + rng.range(-0.8, 0.8)]);
  pts.push([W, 6 + rng.range(-0.8, 0.8)]);
  let len = 0;
  for (let i = 1; i < pts.length; i++) len += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
  const main = { d: polyline(pts), len: Math.ceil(len) + 1 };

  const yAt = (x) => {
    for (let i = 1; i < pts.length; i++) {
      if (x <= pts[i][0]) {
        const [ax, ay] = pts[i - 1];
        const [bx, by] = pts[i];
        return bx === ax ? by : ay + ((by - ay) * (x - ax)) / (bx - ax);
      }
    }
    return 6;
  };

  const branches = [];
  const ticks = [];
  let spore = null;
  let pin = null;

  if (pattern === 'branches' || pattern === 'roots' || pattern === 'spore') {
    const roots = pattern === 'roots';
    const n = pattern === 'spore' ? 4 : clamp(Math.round(W / 120), 3, 9);
    let up = rng.chance(0.5);
    for (let i = 0; i < n; i++) {
      const at = clamp((i + 0.5) / n + rng.range(-0.04, 0.04), 0, 1);
      const x = at * W;
      const y = yAt(x);
      const l = rng.range(4, 7);
      const ang = (roots ? rng.range(45, 65) : rng.range(55, 70)) * DEG;
      if (!roots && rng.chance(0.7)) up = !up;
      const dirY = roots ? 1 : up ? -1 : 1;
      const ex = x + Math.cos(ang) * l;
      const ey = y + dirY * Math.sin(ang) * l;
      branches.push({ d: polyline([[x, y], [ex, ey]]), len: Math.ceil(l) + 1, at });
    }
    if (roots) pin = { x: W - 8 };
    if (pattern === 'spore') spore = { cx: 3, cy: 6 };
  } else if (pattern === 'septate') {
    for (let x = 56; x < W - 8; x += 56) {
      ticks.push({ d: `M${f1(x)} 2V10M${f1(x + 3)} 6Q${f1(x)} 1 ${f1(x - 3)} 4`, at: x / W });
    }
  }
  return { main, branches, ticks, spore, pin };
}
